import { useState, useEffect, useCallback, useRef } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { focusAfterPaint } from '@/utils/dom';

export interface AddCustomItemModalProps {
  open: boolean;
  title: string;            // e.g. 'Add Custom Disposition'
  fieldLabel: string;       // e.g. 'Disposition'
  placeholder?: string;
  maxLength?: number;       // ViolationType has 255
  requiredMessage?: string;
  inputClassName?: string;
  theme?: string;
  onSave: (value: string) => void;
  onCancel: () => void;
}

export function AddCustomItemModal({
  open,
  title,
  fieldLabel,
  placeholder = 'Enter value...',
  maxLength,
  requiredMessage = 'Value required.',
  inputClassName = 'kstc-input',
  theme,
  onSave,
  onCancel,
}: AddCustomItemModalProps) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset form and focus input when opening
  useEffect(() => {
    if (open) {
      setValue('');
      focusAfterPaint(inputRef);
    }
  }, [open]);

  const handleSave = useCallback(() => {
    if (!value || !value.trim()) {
      (window as any).App?.showPopupNotification?.(requiredMessage, 'error');
      return;
    }

    const trimmed = value.trim();

    // Check for disallowed characters
    if (trimmed.includes('[[') || trimmed.includes(']]')) {
      (window as any).App?.showPopupNotification?.(`${fieldLabel} cannot include "[[" or "]]".`, 'error');
      return;
    }

    // Validate maxLength if specified
    if (maxLength && trimmed.length > maxLength) {
      (window as any).App?.showPopupNotification?.(`${fieldLabel} cannot exceed ${maxLength} characters.`, 'error');
      return;
    }

    onSave(trimmed);
  }, [value, requiredMessage, fieldLabel, maxLength, onSave]);

  return (
    <BaseFormModal
      open={open}
      title={title}
      theme={theme}
      dialogClass="kstc-dialog--form"
      saveText="Add"
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div className="kstc-form-group">
        <label className="kstc-label">{fieldLabel}</label>
        <input
          type="text"
          className={inputClassName}
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
        />
      </div>
    </BaseFormModal>
  );
}
